import React, {useCallback} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import DropdownContainer from '../../../components/core/dropdown/DropdownContainer';
import DropdownRow from "../../../components/core/dropdown/DropdownRow";
import {useResultsTableDimensions, useBoolean, useToggle} from '../../../utils/hooks';
import SMCTable from '../../../components/core/SMCTable/SMCTable';
import GatewayWizard from '../../Wizards/GatewayWizard';
import {useGatewayResults} from './gatewayResultsHelper';
import {gatewayPermissionsSelector} from '../../../store/selectors/user.selectors';
import GatewayFilterPane from '../../Filters/GatewayFilterPane';
import GatewayMoveToPlanModal from '../../PopupPages/GatewayMoveToPlanModal';
import {helpContextPageUrl} from '../../../conf/Routes';
import SMCTableActions from '../../../components/core/SMCTableActions/SMCTableActions';
import {GatewayFilterSchema} from '../../../api/DBSchema/Gateway';


function GatewayResults({
    urlParams,
    gatewayPermissions
}) {
    const { t } = useTranslation();

    const [tableWidth, tableHeight] = useResultsTableDimensions();
    const [isNewObjectWindowOpen, openNewObjectWindow, closeNewObjectWindow] = useBoolean(false);
    const [isMoveToPlanModalOpen, openMoveToPlanModal, closeMoveToPlanModal] = useBoolean(false);
    const [isFilterPaneOpen, toggleFilterPane] = useToggle(false);

    const {
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        columnsWidths,
        setColumnsWidths,
        reloadButtonOnClick,
        deleteButtonOnClick,
        newObjectOnSuccess,
        resetToPlanAction,
        toExcelAction,
        rebootAction,
        fetchAction,
        backupAction,
        sendDailyReportAction,
        sendWeeklyReportAction,
        sendMonthlyReportAction
    } = useGatewayResults(urlParams);

    const handleOpenMoveToPlanModal = useCallback(() => {
        if (!selectedObjects || !selectedObjects.length) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        openMoveToPlanModal();
    }, [selectedObjects, openMoveToPlanModal, t]);

    const canDelete = gatewayPermissions ? gatewayPermissions.delete : false;
    const canCreate = gatewayPermissions ? gatewayPermissions.create : false;

      const GatewayActionsMenu = () => {
        return (
                 <DropdownContainer >
                    <DropdownRow divider={true}>General Actions</DropdownRow> 
                    <DropdownRow text={t('ACTION_RESET_TOPLAN')} action={resetToPlanAction} />
                    <DropdownRow text="Move to Plan" action={handleOpenMoveToPlanModal} />
                    <DropdownRow text="To Excel" action={toExcelAction} />
                    <DropdownRow divider={true}>Remote Actions</DropdownRow> 
                    <DropdownRow text="Reboot" action={rebootAction} />
                    <DropdownRow text="Fetch Now" action={fetchAction} />
                    <DropdownRow text="Backup Now" action={backupAction} />
                    <DropdownRow divider={true}>Report</DropdownRow> 
                    <DropdownRow text="Send Daily Report" action={sendDailyReportAction} />
                    <DropdownRow text="Send Weekly Report" action={sendWeeklyReportAction} />
                    <DropdownRow text="Send Monthly Report" action={sendMonthlyReportAction} />
                </DropdownContainer>
        );
      };

    return (
        <SMCPage loading={!isPageLoaded} helpId={helpContextPageUrl.hasHAstatus}>        
            <div className="DrawResultsDiv">  
                <SMCTableActions
                    openNewObjectWindow={openNewObjectWindow}
                    reloadButtonOnClick={reloadButtonOnClick}
                    deleteButtonOnClick={deleteButtonOnClick}
                    toggleFilterPane={toggleFilterPane}
                    canCreate={canCreate}
                    canDelete={canDelete}
                    isFetching={isFetching}
                    actions={GatewayActionsMenu}
                    tableContent={tableContent}
                    isFilterPaneOpen={isFilterPaneOpen}
                    selectedObjects={selectedObjects}
                    initialSearch={filterObject}
                    onSearchChange={setFilterObject}
                    searchAttribute={GatewayFilterSchema.freeTextSearch}
                    isDataFiltered={() => {
                        return (
                            (Object.keys(filterObject).length > 3 ||
                            ("connected" in filterObject && filterObject["connected"]?.value !== true) ||
                            ("disconnected" in filterObject && filterObject["disconnected"]?.value !== true) ||
                            ("disabled" in filterObject && filterObject["disabled"]?.value !== true) ||
                            ("freeTextSearch" in filterObject))
                        )
                    }}
                /> 
                <SMCTable
                    columns={columns}
                    columnsState={columnsState}
                    setColumnsState={setColumnsState}
                    setDefaultColumnsState={setDefaultColumnsState}
                    isFilterOpen={isFilterPaneOpen}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={setSelectedObjects}
                    hasCheckAll={true}
                    defaultColumnsWidths={columnsWidths}
                    onEndResizingColumns={setColumnsWidths}
                    content={tableContent}
                    sortBasis={sortBasis}
                    sortDirection={sortDirection}
                    setSort={setSort}
                    loading={isFetching}
                    onRequestLoadPage={fetchNextPage}
                    isLastPage={isLastPage}
                    width={tableWidth}
                    height={tableHeight}
                    rowIdSelector={rowIdSelector}
                    filterElem={
                        isFilterPaneOpen &&
                        <GatewayFilterPane
                            initialFilter={filterObject}
                            onFilterChange={setFilterObject}
                            open={isFilterPaneOpen}
                        />
                    }
                    textToHighlight={freeTextSearch}
                    showEditIcon={false}
                />
                {/* Floating windows */}
                <GatewayWizard
                    isOpen={isNewObjectWindowOpen}
                    onRequestClose={closeNewObjectWindow}
                    onSuccess={newObjectOnSuccess}
                />
                {isMoveToPlanModalOpen && <GatewayMoveToPlanModal
                    isOpen={isMoveToPlanModalOpen}
                    onRequestClose={closeMoveToPlanModal}
                    selectedGateways={selectedObjects}
                    onSuccess={() => {
                        reloadButtonOnClick();
                        closeMoveToPlanModal();
                    }}
                />}
            </div>
        </SMCPage>
    );
}

const mapStateToProps = state => ({
    urlParams: state.router.location.search,
    gatewayPermissions: gatewayPermissionsSelector(state)
});

const mapDispatchToProps = null;

export default connect(mapStateToProps, mapDispatchToProps)(GatewayResults);